import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import api from '../services/api'

export default function SignPage() {
  const { token } = useParams()
  const [contract, setContract] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [step, setStep] = useState(0)
  const [pdpaAccepted, setPdpaAccepted] = useState(false)
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [idCardInput, setIdCardInput] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpBusy, setOtpBusy] = useState(false)
  const [signingSession, setSigningSession] = useState('')
  const [verifyError, setVerifyError] = useState('')
  const [form, setForm] = useState({
    first_name: '', last_name: '', address: '', phone: '', employee_note: '',
    job_duties: '', job_description: ''
  })
  const [hasSig, setHasSig] = useState(false)
  const [sigMode, setSigMode] = useState('draw')
  const [uploadedSig, setUploadedSig] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const canvasRef = useRef(null)
  const isDrawingRef = useRef(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchContract() }, [token])

  useEffect(() => {
    if (step === 3 && sigMode === 'draw') setTimeout(() => initCanvas(), 300)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, sigMode])

  const fetchContract = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/contracts/sign/${token}`)
      if (res.data.success) {
        setContract(res.data.data)
        setForm(prev => ({ ...prev, employee_note: '' }))
      }
    } catch (err) { setError(err.response?.data?.message || 'ไม่พบสัญญา หรือลิงก์หมดอายุแล้ว') }
    finally { setLoading(false) }
  }

  const initCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const newCanvas = canvas.cloneNode(true)
    canvas.parentNode.replaceChild(newCanvas, canvas)
    canvasRef.current = newCanvas
    const c = canvasRef.current
    const cx = c.getContext('2d')
    cx.strokeStyle = '#1a1a1a'
    cx.lineWidth = 2.5
    cx.lineCap = 'round'
    cx.lineJoin = 'round'
    const getPos = (e) => {
      const rect = c.getBoundingClientRect()
      const scaleX = c.width / rect.width
      const scaleY = c.height / rect.height
      if (e.touches) return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY }
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
    }
    c.onmousedown = c.ontouchstart = (e) => {
      e.preventDefault()
      isDrawingRef.current = true
      const p = getPos(e)
      cx.beginPath()
      cx.moveTo(p.x, p.y)
    }
    c.onmousemove = c.ontouchmove = (e) => {
      e.preventDefault()
      if (!isDrawingRef.current) return
      const p = getPos(e)
      cx.lineTo(p.x, p.y)
      cx.stroke()
      setHasSig(true)
    }
    c.onmouseup = c.ontouchend = () => { isDrawingRef.current = false }
    c.onmouseleave = () => { isDrawingRef.current = false }
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    setHasSig(false)
  }

  const handleChangeSigMode = (mode) => {
    setSigMode(mode)
    setHasSig(false)
    setUploadedSig(null)
    if (mode === 'draw') {
      setTimeout(() => {
        const canvas = canvasRef.current
        if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
      }, 350)
    }
  }

  const handleSubmit = async () => {
    if (sigMode === 'draw') { if (!hasSig) { alert('กรุณาวาดลายเซ็นก่อน'); return } }
    else { if (!uploadedSig) { alert('กรุณาอัปโหลดรูปลายเซ็นก่อน'); return } }

    try {
      setSubmitting(true)
      let signature
      if (sigMode === 'upload') signature = uploadedSig
      else {
        const canvas = canvasRef.current
        if (!canvas) { alert('เกิดข้อผิดพลาด ไม่พบ canvas'); return }
        signature = canvas.toDataURL('image/png')
      }
      await api.post(`/contracts/sign/${token}`, {
        signature, pdpa_accepted: true, employee_note: form.employee_note,
        signing_session: signingSession
      }, { headers: { 'X-Signing-Session': signingSession } })
      setDone(true)
    } catch (err) { alert(err.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง') }
    finally { setSubmitting(false) }
  }

  const requestOtp = async () => {
    const idCard = idCardInput.replace(/\D/g, '')
    if (idCard.length !== 13) { setVerifyError('กรุณากรอกเลขบัตรประชาชน 13 หลัก'); return }
    try {
      setOtpBusy(true); setVerifyError('')
      const res = await api.post(`/contracts/sign/${token}/request-otp`, { id_card: idCard })
      setOtpSent(true)
      setVerifyError(res.data.message)
    } catch (err) { setVerifyError(err.response?.data?.message || 'ไม่สามารถส่ง OTP ได้') }
    finally { setOtpBusy(false) }
  }

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) { setVerifyError('กรุณากรอก OTP 6 หลัก'); return }
    try {
      setOtpBusy(true); setVerifyError('')
      const res = await api.post(`/contracts/sign/${token}/verify-otp`, { otp })
      setSigningSession(res.data.signing_session)
      setStep(1)
    } catch (err) { setVerifyError(err.response?.data?.message || 'OTP ไม่ถูกต้อง') }
    finally { setOtpBusy(false) }
  }

  const inputStyle = { width: '100%', padding: '9px 12px', border: '0.5px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', color: '#0f172a', background: 'white', outline: 'none', fontFamily: 'inherit' }
  const labelStyle = { display: 'block', fontSize: '12px', color: '#374151', marginBottom: '5px' }
  const cardStyle = { background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', padding: '20px', marginBottom: '14px' }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#94a3b8', fontSize: '13px' }}>กำลังโหลด...</p>
    </div>
  )

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ textAlign: 'center', maxWidth: '360px' }}>
        <div style={{ width: '64px', height: '64px', background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <i className="ti ti-x" style={{ fontSize: '28px', color: '#ef4444' }} />
        </div>
        <h2 style={{ fontSize: '16px', fontWeight: '500', color: '#0f172a', marginBottom: '8px' }}>ไม่สามารถเข้าถึงได้</h2>
        <p style={{ fontSize: '13px', color: '#94a3b8' }}>{error}</p>
      </div>
    </div>
  )

  if (done) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ textAlign: 'center', maxWidth: '360px' }}>
        <div style={{ width: '64px', height: '64px', background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <i className="ti ti-circle-check" style={{ fontSize: '32px', color: '#22c55e' }} />
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: '500', color: '#0f172a', marginBottom: '8px' }}>เซ็นสัญญาสำเร็จแล้ว</h2>
        <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px' }}>ขอบคุณที่เซ็นสัญญาจ้างพนักงาน</p>
        <p style={{ fontSize: '13px', color: '#94a3b8' }}>เทศบาลนครมาบตาพุด</p>
        <div style={{ marginTop: '24px', background: '#fef9c3', borderRadius: '10px', padding: '14px' }}>
          <p style={{ fontSize: '12px', color: '#713f12' }}>ลิงก์นี้หมดอายุแล้ว ไม่สามารถใช้งานได้อีก</p>
        </div>
      </div>
    </div>
  )

  return (
    <div className="sign-public-page" style={{ minHeight: '100vh', background: '#f3faff' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '0.5px solid #e2e8f0', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ width: '36px', height: '36px', background: '#075dcc', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-file-certificate" style={{ color: 'white', fontSize: '18px' }} />
        </div>
        <div>
          <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>สัญญาจ้างพนักงาน</p>
          <p style={{ fontSize: '11px', color: '#94a3b8' }}>เทศบาลนครมาบตาพุด</p>
        </div>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', background: 'white', borderBottom: '0.5px solid #e2e8f0' }}>
        {['ยืนยันตัวตน', 'PDPA', 'ตรวจสอบ', 'ลงลายเซ็น'].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '10px 4px', textAlign: 'center', fontSize: '11px', fontWeight: '500', borderBottom: '2px solid', borderColor: step === i ? '#075dcc' : step > i ? '#22c55e' : 'transparent', color: step === i ? '#075dcc' : step > i ? '#22c55e' : '#cbd5e1' }}>
            {s}
          </div>
        ))}
      </div>

      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '16px' }}>

        {/* Step 0: ยืนยันตัวตน */}
        {step === 0 && (
          <div>
            <div style={cardStyle}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ width: '64px', height: '64px', background: '#e9f6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <i className="ti ti-id" style={{ fontSize: '28px', color: '#075dcc' }} />
                </div>
                <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>ยืนยันตัวตน</p>
              <p style={{ fontSize: '12px', color: '#94a3b8' }}>กรอกเลขบัตรประชาชน แล้วรับ OTP ทางอีเมลที่ลงทะเบียน</p>
              </div>
              <label style={labelStyle}>เลขบัตรประชาชน 13 หลัก</label>
              <input type="text" inputMode="numeric" autoComplete="off" value={idCardInput}
                onChange={e => { setIdCardInput(e.target.value); setVerifyError('') }}
                placeholder="กรอกเลขบัตรประชาชน" maxLength={13}
                style={{ ...inputStyle, textAlign: 'center', fontSize: '18px', letterSpacing: '0.2em', padding: '12px' }} />
              {otpSent && <><label style={{ ...labelStyle, marginTop: '14px' }}>รหัส OTP 6 หลัก</label><input type="text" inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} style={{ ...inputStyle, textAlign: 'center', fontSize: '20px', letterSpacing: '0.35em', padding: '12px' }} /><p style={{fontSize:'11px',color:'#64748b',marginTop:'6px'}}>ส่งไปยัง {contract?.email_masked} และหมดอายุใน 5 นาที</p></>}
              {verifyError && <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>{verifyError}</p>}
            </div>
            <button className="ice-primary-action" onClick={otpSent ? verifyOtp : requestOtp} disabled={otpBusy} style={{ width: '100%', padding: '12px', background: '#075dcc', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: otpBusy ? 'wait' : 'pointer' }}>
              {otpBusy ? 'กำลังดำเนินการ...' : otpSent ? 'ยืนยัน OTP' : 'ส่ง OTP'}
            </button>
          </div>
        )}

        {/* Step 1: PDPA */}
        {step === 1 && (
          <div>
            <div style={cardStyle}>
              <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a', marginBottom: '12px' }}>นโยบายคุ้มครองข้อมูลส่วนบุคคล</p>
              <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '256px', overflowY: 'auto' }}>
                <p>เทศบาลนครมาบตาพุด ให้ความสำคัญกับการคุ้มครองข้อมูลส่วนบุคคลของท่าน ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA)</p>
                <p style={{ fontWeight: '500', color: '#0f172a' }}>ข้อมูลที่เราเก็บรวบรวม</p>
                <p>ชื่อ-นามสกุล, เลขบัตรประชาชน, ที่อยู่, เบอร์โทรศัพท์, ลายเซ็น และข้อมูลที่เกี่ยวข้องกับสัญญาจ้างงาน</p>
                <p style={{ fontWeight: '500', color: '#0f172a' }}>วัตถุประสงค์การใช้ข้อมูล</p>
                <p>เพื่อจัดทำสัญญาจ้างพนักงาน และเก็บเป็นหลักฐานทางราชการตามระเบียบที่เกี่ยวข้อง</p>
                <p style={{ fontWeight: '500', color: '#0f172a' }}>การเก็บรักษาข้อมูล</p>
                <p>จัดเก็บต่อเนื่องตลอดอายุการจ้างงาน และหลังจากนั้นตามระยะเวลาที่กฎหมาย ระเบียบ และงานสารบรรณกำหนด โดยมีมาตรการรักษาความมั่นคงปลอดภัยที่เหมาะสม</p>
                <p style={{ fontWeight: '500', color: '#0f172a' }}>สิทธิ์ของท่าน</p>
                <p>ท่านมีสิทธิ์ในการเข้าถึง แก้ไข หรือขอลบข้อมูลส่วนบุคคลของท่านได้ โดยติดต่อเจ้าหน้าที่ที่ดูแลระบบ</p>
              </div>
            </div>

            <div style={{ background: '#e9f6ff', borderRadius: '14px', padding: '14px', marginBottom: '14px', display: 'flex', gap: '10px' }}>
              <i className="ti ti-info-circle" style={{ fontSize: '18px', color: '#075dcc', flexShrink: 0, marginTop: '1px' }} />
              <p style={{ fontSize: '12px', color: '#0646a6', lineHeight: '1.6' }}>
                <strong>ข้อกำหนดสัญญาอิเล็กทรอนิกส์</strong><br />
                สัญญาฉบับนี้จัดทำด้วยระบบอิเล็กทรอนิกส์ คู่สัญญาได้อ่านและพิจารณาข้อความโดยละเอียดแล้วจึงลงลายมือชื่ออิเล็กทรอนิกส์ และต่างเก็บรักษาสัญญาในรูปแบบของข้อมูลอิเล็กทรอนิกส์ไว้ในระบบคอมพิวเตอร์ของตน
              </p>
            </div>

            <div style={cardStyle}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={pdpaAccepted} onChange={e => setPdpaAccepted(e.target.checked)} style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', color: '#374151' }}>ข้าพเจ้าได้อ่านและยอมรับนโยบายคุ้มครองข้อมูลส่วนบุคคลของเทศบาลนครมาบตาพุด</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginTop:'12px' }}>
                <input type="checkbox" checked={consentAccepted} onChange={e => setConsentAccepted(e.target.checked)} style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', color: '#374151' }}>ข้าพเจ้ายินยอมให้เทศบาลนครมาบตาพุดเก็บรวบรวมและใช้ข้อมูลตามวัตถุประสงค์ที่แจ้งไว้</span>
              </label>
            </div>

            <button className="ice-primary-action" onClick={() => setStep(2)} disabled={!pdpaAccepted || !consentAccepted}
              style={{ width: '100%', padding: '12px', background: pdpaAccepted && consentAccepted ? '#075dcc' : '#b8e1fa', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: pdpaAccepted && consentAccepted ? 'pointer' : 'not-allowed' }}>
              ยอมรับและดำเนินการต่อ
            </button>
          </div>
        )}

        {/* Step 2: ตรวจสอบข้อมูล */}
        {step === 2 && (
          <div>
            <div style={cardStyle}>
              <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>ตรวจสอบข้อมูล</p>
              <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '14px' }}>ข้อมูลประจำตัวถูกล็อก หากไม่ถูกต้องกรุณาหยุดลงนามและติดต่อ HR</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background:'#f8fafc', borderRadius:'10px', padding:'12px' }}><label style={labelStyle}>ชื่อพนักงาน</label><strong>{contract?.employee_name}</strong></div>
                <div><label style={labelStyle}>หมายเหตุ (ถ้ามี)</label><input type="text" value={form.employee_note} onChange={e => setForm({ ...form, employee_note: e.target.value })} placeholder="หมายเหตุเพิ่มเติม" style={inputStyle} /></div>
              </div>
            </div>

            <div style={{ background: '#e9f6ff', borderRadius: '14px', padding: '16px', marginBottom: '14px' }}>
              <p style={{ fontSize: '12px', fontWeight: '500', color: '#07377f', marginBottom: '12px' }}>ข้อมูลสัญญา</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  ['ชื่อพนักงาน', contract?.employee_name || '-'],
                  ['เลขสัญญา', contract?.contract_no],
                  ['ประเภท', contract?.emp_type],
                  ['วันเริ่ม', contract?.start_date && new Date(contract.start_date).toLocaleDateString('th-TH')],
                  ['วันสิ้นสุด', contract?.end_date && new Date(contract.end_date).toLocaleDateString('th-TH')],
                  ['ค่าตอบแทน', contract?.total_salary && `${Number(contract.total_salary).toLocaleString('th-TH')} บาท/เดือน`],
                  ['กองงาน', contract?.division_name || '-'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p style={{ fontSize: '11px', color: '#1489d9' }}>{label}</p>
                    <p style={{ fontSize: '13px', fontWeight: '500', color: '#07377f', marginTop: '1px' }}>{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', cursor: 'pointer' }}>ย้อนกลับ</button>
              <button className="ice-primary-action" onClick={() => setStep(3)} style={{ flex: 1, padding: '12px', background: '#075dcc', color: 'white', border: 'none', borderRadius: '12px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>ดำเนินการต่อ</button>
            </div>
          </div>
        )}

        {/* Step 3: ลงลายเซ็น */}
        {step === 3 && (
          <div>
            <div style={cardStyle}>
              <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>ลงลายเซ็น</p>
              <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '14px' }}>เลือกวิธีลงลายเซ็นของท่าน</p>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                <button onClick={() => handleChangeSigMode('draw')}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: '500', border: '1.5px solid', borderColor: sigMode === 'draw' ? '#075dcc' : '#e2e8f0', background: sigMode === 'draw' ? '#075dcc' : 'white', color: sigMode === 'draw' ? 'white' : '#64748b', cursor: 'pointer' }}>
                  วาดลายเซ็น
                </button>
                <button onClick={() => handleChangeSigMode('upload')}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: '500', border: '1.5px solid', borderColor: sigMode === 'upload' ? '#075dcc' : '#e2e8f0', background: sigMode === 'upload' ? '#075dcc' : 'white', color: sigMode === 'upload' ? 'white' : '#64748b', cursor: 'pointer' }}>
                  อัปโหลดรูป
                </button>
              </div>

              {sigMode === 'draw' && (
                <div>
                  <div style={{ border: '2px dashed #e2e8f0', borderRadius: '14px', overflow: 'hidden', background: 'white' }}>
                    <canvas ref={canvasRef} width={600} height={200} style={{ width: '100%', touchAction: 'none', cursor: 'crosshair', display: 'block' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                    <p style={{ fontSize: '11px', color: '#94a3b8' }}>วาดลายเซ็นในกรอบด้านบน</p>
                    <button onClick={clearCanvas} style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>ล้างลายเซ็น</button>
                  </div>
                </div>
              )}

              {sigMode === 'upload' && (
                <div>
                  {!uploadedSig ? (
                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '160px', border: '2px dashed #e2e8f0', borderRadius: '14px', cursor: 'pointer', background: 'white' }}>
                      <i className="ti ti-cloud-upload" style={{ fontSize: '32px', color: '#cbd5e1', marginBottom: '8px' }} />
                      <p style={{ fontSize: '13px', color: '#94a3b8' }}>คลิกเพื่อเลือกรูปลายเซ็น</p>
                      <p style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '4px' }}>รองรับ .jpg, .png (ไม่เกิน 2MB)</p>
                      <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files[0]
                          if (!file) return
                          if (file.size > 2 * 1024 * 1024) { alert('ไฟล์ขนาดใหญ่เกินไป'); return }
                          const reader = new FileReader()
                          reader.onload = ev => { setUploadedSig(ev.target.result); setHasSig(true) }
                          reader.readAsDataURL(file)
                        }} />
                    </label>
                  ) : (
                    <div>
                      <div style={{ border: '0.5px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden', background: 'white', padding: '16px', textAlign: 'center' }}>
                        <img src={uploadedSig} alt="ลายเซ็น" style={{ maxHeight: '128px', margin: '0 auto', objectFit: 'contain' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                        <p style={{ fontSize: '11px', color: '#22c55e' }}>✓ อัปโหลดลายเซ็นแล้ว</p>
                        <button onClick={() => { setUploadedSig(null); setHasSig(false) }} style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>เปลี่ยนรูป</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setStep(2)} style={{ flex: 1, padding: '12px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', cursor: 'pointer' }}>ย้อนกลับ</button>
              <button className="ice-primary-action" onClick={handleSubmit} disabled={submitting || !hasSig}
                style={{ flex: 1, padding: '12px', background: (submitting || !hasSig) ? '#86efac' : '#22c55e', color: 'white', border: 'none', borderRadius: '12px', fontSize: '13px', fontWeight: '500', cursor: (submitting || !hasSig) ? 'not-allowed' : 'pointer' }}>
                {submitting ? 'กำลังบันทึก...' : 'ยืนยันการเซ็นสัญญา'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
